import React from 'react';
import { APP_CONFIG } from '../../configs/config';

const FOOTER_SECTIONS = {
  products: [
    { name: 'TadReamk Eye', url: '/products/tadreamk-eye' },
    { name: 'TadReamk Patent', url: '/products/tadreamk-patent' },
    { name: 'TadReamk Create', url: '/products/tadreamk-create' },
    { name: 'Trademark Trading', url: '/products/trademark-trading' },
    { name: 'TadReamk Surveillance', url: '/products/tadreamk-surveillance' },
    { name: 'Business Card Manager', url: '/products/business-card-manager' }
  ],
  company: [
    { name: 'About Us', url: '/about' },
    { name: 'Careers', url: '/careers' }
  ],
  articles: [
    { name: 'News', url: '/news' },
    { name: 'IP Law', url: '/magazine/law' },
    { name: 'TadReamk AI', url: '/magazine/ai' },
    { name: 'TadReamk Design', url: '/magazine/design' }
  ],
  business: [
    { name: 'Offers', url: '/business/overview' },
    { name: 'API Documentation', url: '/business/api' },
    { name: 'Contact Sales', url: '/business/contact' },
    { name: 'Pricing', url: '/business/pricing' }
  ],
  legal: [
    { name: 'Privacy Policy', url: '#privacy' },
    { name: 'Terms of Service', url: '#terms' },
    { name: 'Cookie Policy', url: '#cookies' },
    { name: 'GDPR', url: '#gdpr' }
  ]
};

function ExternalLink({ to, children }) {
  const url = to.startsWith('http')
    ? to
    : `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}${to}`;
  return (
    <a
      href={url}
      className="text-gray-300 hover:text-violet-300 transition-colors duration-300 text-sm"
    >
      {children}
    </a>
  );
}

function FooterSection({ title, links }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-white mb-4">{title}</h4>
      <ul className="space-y-3">
        {links.map((link, idx) => (
          <li key={idx}>
            <ExternalLink to={link.url}>{link.name}</ExternalLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-gray-900/50 backdrop-blur border-t border-gray-700/50 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img src={APP_CONFIG.LOGO_URL} alt="TadReamk Logo" className="h-10 w-10 object-contain" />
              <span className="text-2xl font-bold text-white">TadReamk</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-md">
              Empowering innovation through comprehensive trademark and patent
              solutions. Protect, monitor, and trade your intellectual property
              with confidence.
            </p>
            <div className="flex space-x-4">
              <a
                href={APP_CONFIG.SOCIAL_LINKS.LINKEDIN}
                className="w-10 h-10 bg-gray-800/50 hover:bg-gradient-to-r hover:from-violet-600 hover:to-purple-600 border border-gray-700/50 hover:border-violet-400/50 rounded-xl flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hover:scale-105"
                title="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href={APP_CONFIG.SOCIAL_LINKS.GITHUB}
                className="w-10 h-10 bg-gray-800/50 hover:bg-gradient-to-r hover:from-violet-600 hover:to-purple-600 border border-gray-700/50 hover:border-violet-400/50 rounded-xl flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 hover:scale-105"
                title="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          <FooterSection title="Products" links={FOOTER_SECTIONS.products} />
          <FooterSection title="Business" links={FOOTER_SECTIONS.business} />
          <FooterSection title="Articles" links={FOOTER_SECTIONS.articles} />
          <FooterSection title="Company" links={FOOTER_SECTIONS.company} />
        </div>

        <div className="md:hidden mt-8">
          <FooterSection title="Legal" links={FOOTER_SECTIONS.legal} />
        </div>
      </div>

      <div className="border-t border-gray-700/50 bg-gray-900/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">© {new Date().getFullYear()} TadReamk. All rights reserved.</div>
            <div className="hidden md:flex space-x-6">
              {FOOTER_SECTIONS.legal.map((link, idx) => (
                <ExternalLink key={idx} to={link.url}>{link.name}</ExternalLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}


